import React, { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";

import { Collection } from "../../generated/gql/graphql";
import useSharedCollections from "../../hooks/query/collection/use-shared-collections";
import useText from "../../hooks/use-text";
import CollectionCard from "../../pages/collection/parts/collection-card/collection-card";
import CollectionList from "../../pages/collection/parts/collection-list/collection-list";
import CollectionListItem from "../../pages/collection/parts/collection-list/collection-list-item";
import CollectionFilter from "../../pages/collection/parts/filter/filter";
import { UserContext } from "../../providers/user-provider";
import { ListLayout } from "../../types/list";
import { parseType } from "../../utils/graphql";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";

import SectionHeader from "./section-header";

export default function SharedCollections(): React.ReactElement {
  const { me } = useContext(UserContext);
  const [sharedCollections, setSharedCollections] = useState<Collection[]>([]);
  const { data: sharedCollectionsResult } = useSharedCollections(
    me?.id || "-1"
  );
  const [cookies] = useCookies();
  const { strings } = useText(local);

  useEffect(() => {
    if (!sharedCollectionsResult) {
      return;
    }

    setSharedCollections(
      parseType<Collection[]>("sharedCollections", sharedCollectionsResult)
    );
  }, [sharedCollectionsResult]);

  if (!sharedCollections || sharedCollections?.length === 0) {
    return (
      <div className="space-y-10">
        <SectionHeader header={strings.header} count={0} />
        <div className="text-[24px] text-neutral-400">
          <div>{strings.empty.header}</div>
          <span className="text-[16px]">{strings.empty.description}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <SectionHeader
        header={strings.header}
        count={sharedCollections.length || 0}
      />
      <CollectionFilter />
      {cookies.listLayout === ListLayout.Grid ? (
        sharedCollections.map((collection) => (
          <CollectionCard key={collection.id} collection={collection} />
        ))
      ) : (
        <CollectionList>
          {sharedCollections.map((collection) => (
            <CollectionListItem key={collection.id} collection={collection} />
          ))}
        </CollectionList>
      )}
    </div>
  );
}

type LocalSchema = {
  header: string;
  empty: {
    header: string;
    description: string;
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    header: "Collections shared with you",
    empty: {
      header: "No collection has been shared with you yet.",
      description: "Team members can invite you to their collection.",
    },
  },
};
